/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React from 'react';
import { Button, Typography } from '@material-ui/core';
import { CodeSnippet, EmptyState } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import CliBackstage from './assets/cli_backstage.png';

const EXAMPLE = `apiVersion: backstage.io/v1alpha1
  kind: Component
  metadata:
    name: <github-repo>
    title: Sample Service # human readable component name
    description: My first Backstage catalog item.
    annotations:
      backstage.io/source-location: url:<github-project-url>
  spec:
    type: library
    owner: user:<github-username>
    lifecycle: development
`;

export const Instructions = () => {
  const configApi = useApi(configApiRef);
  const appBaseUrl = configApi.getOptionalString('app.baseUrl');
  return (
    <EmptyState
      missing={{
        customImage: (
          <>
            <img
              src={CliBackstage}
              alt="Adding a Component with CLI Backstage"
              style={{ maxWidth: '576px' }}
            />
            <a
              href="https://www.loom.com/share/b96f07f0579a423f8cb762e8c1b7f3fe?t=23"
              title="Adding a Component"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <img
                src="https://cdn.loom.com/sessions/thumbnails/b96f07f0579a423f8cb762e8c1b7f3fe-1652369414749-with-play.gif"
                alt="Adding a Component"
              />
            </a>
          </>
        ),
      }}
      title=""
      description="These instructions will enable you to share your projects in the Global Catalog, driving collaboration and innovation within InnerSource communities across the business units."
      action={
        <>
          <Typography variant="body1">
            To prevent errors and make it easier to add new components, use the{' '}
            <code>cli-backstage</code> tool. To do this:
          </Typography>
          <Typography variant="body1">
            Navigate to the root of your GitHub repository in your terminal and
            execute the following command:
          </Typography>
          <CodeSnippet
            text="npx cli-backstage ."
            language="bash"
            showLineNumbers
            highlightedNumbers={[4, 8, 11]}
            customStyle={{
              background: 'inherit',
              fontSize: '115%',
              paddingLeft: '1em',
            }}
          />

          <Typography variant="body1">
            By using the{' '}
            <a
              href="https://github.com/newscorp-ghfb/global-backstage-cli"
              target="_blank"
            >
              <code>cli-backstage</code>
            </a>{' '}
            tool, you will also help get your projects a step closer to reaching
            InnerSource Level 1. For more information, visit the{' '}
            <a
              href={`${appBaseUrl}/docs/default/component/innersource-101/how_to/innersource_with_backstage/publish_project/`}
              target="_blank"
            >
              InnerSource 101 Tech Docs
            </a>
            .
          </Typography>
          <hr style={{ marginTop: '30px', marginBottom: '30px' }} />
          <Typography variant="body1">
            <b>ALTERNATIVELY</b>, to upload your projects manually individually,
            follow these steps below:
          </Typography>
          <CodeSnippet
            text={EXAMPLE}
            language="yaml"
            showLineNumbers
            highlightedNumbers={[4, 8, 11]}
            customStyle={{ background: 'inherit', fontSize: '115%' }}
          />
          <Typography variant="body1">
            Import Your Component YAML file into Backstage
          </Typography>
          <Typography variant="body1">
            Follow video instructions to add a component to the Backstage
            catalog.
          </Typography>
          <br />
          <Button
            style={{
              backgroundColor: 'rgb(224, 224, 224)',
              padding: '6px 16px',
            }}
            href="https://backstage.io/docs/features/software-catalog/descriptor-format/#contents"
          >
            YAML format details
          </Button>
        </>
      }
    />
  );
};
